// import { useRef } from "react";

// interface OTPInputProps {
//   value: string;
//   onChange: (code: string) => void;
//   length?: number;
// }

// export default function OTPInput({ value, onChange, length = 6 }: OTPInputProps) {
//   const inputRefs = useRef<HTMLInputElement[]>([]);

//   const handleChange = (val: string, index: number) => {
//     if (!/^\d?$/.test(val)) return;

//     const arr = value.padEnd(length, " ").split("");
//     arr[index] = val;
//     const newCode = arr.join("").trimEnd();
//     onChange(newCode);

//     // Vai avanti solo se c’è un valore e non siamo all’ultimo input
//     if (val && index < length - 1) {
//       inputRefs.current[index + 1]?.focus();
//     }
//   };

//   const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
//     if (e.key === "Backspace") {
//       const arr = value.split("");

//       // Cancella il valore corrente, NON muoverti
//       arr[index] = "";
//       onChange(arr.join(""));
//     }
//   };

//   const handlePaste = (e: React.ClipboardEvent) => {
//     const paste = e.clipboardData
//       .getData("text")
//       .replace(/\D/g, "")
//       .slice(0, length);

//     if (!paste) return;

//     onChange(paste);

//     paste.split("").forEach((char, i) => {
//       const el = inputRefs.current[i];
//       if (el) el.value = char;
//     });

//     inputRefs.current[Math.min(paste.length, length) - 1]?.focus();
//   };

//   return (
//     <div className="flex gap-2 justify-center" onPaste={handlePaste}>
//       {Array.from({ length }).map((_, i) => (
//         <input
//           key={i}
//           ref={(el) => {
//             if (el) inputRefs.current[i] = el;
//           }}
//           type="text"
//           maxLength={1}
//           className="w-12 h-12 border rounded text-center text-xl"
//           onChange={(e) => handleChange(e.target.value, i)}
//           onKeyDown={(e) => handleKeyDown(e, i)}
//           autoFocus={i === 0}
//         />
//       ))}
//     </div>
//   );
// }




import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";

interface OTPInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
}

export interface OTPInputRef {
  focus: () => void;
}

const OTPInput = forwardRef<OTPInputRef, OTPInputProps>(
  ({ value, onChange, length = 6 }, ref) => {
    const inputRefs = useRef<HTMLInputElement[]>([]);

    // Esponi il metodo focus al parent
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRefs.current[0]?.focus();
      },
    }));

    // Sincronizza gli input con il valore
    useEffect(() => {
      const digits = value.padEnd(length, "").split("");
      inputRefs.current.forEach((input, i) => {
        if (input) input.value = digits[i] || "";
      });
    }, [value, length]);

    const handleChange = (val: string, index: number) => {
      if (!/^\d?$/.test(val)) return;

      const arr = value.padEnd(length, "").split("");
      arr[index] = val;
      const newCode = arr.join("").replace(/\s+$/, "");
      onChange(newCode);

      if (val && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
      if (e.key === "Backspace") {
        const arr = value.padEnd(length, "").split("");
        
        if (arr[index]) {
          arr[index] = "";
          onChange(arr.join("").replace(/\s+$/, ""));
        } else if (index > 0) {
          arr[index - 1] = "";
          onChange(arr.join("").replace(/\s+$/, ""));
          inputRefs.current[index - 1]?.focus();
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const paste = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, length);

      if (!paste) return;

      onChange(paste);

      const focusIndex = Math.min(paste.length, length - 1);
      setTimeout(() => inputRefs.current[focusIndex]?.focus(), 0);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    return (
      <div className="flex gap-2 justify-center">
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) => {
              if (el) inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className="w-12 h-14 border-2 border-gray-300 rounded-lg text-center text-2xl font-semibold focus:border-blue-500 focus:outline-none transition-colors"
            onChange={(e) => handleChange(e.target.value, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            autoComplete="off"
          />
        ))}
      </div>
    );
  }
);

OTPInput.displayName = "OTPInput";

export default OTPInput;